import { motion } from 'framer-motion'
import { Github, Tag, Star, GitFork, Eye, BookOpen } from 'lucide-react'
import { useState } from 'react'
import { useGitHubStatsWithCache } from '../../hooks/useGitHubStats'

interface Project {
  name: string
  description: string
  thumbnail: string
  tags: string[]
  github_url: string
  docs_url?: string
}

interface ProjectCardProps {
  project: Project
}

const ProjectCard = ({ project }: ProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false)
  
  // 获取真实的GitHub统计数据
  const { stats: githubStats, loading: statsLoading } = useGitHubStatsWithCache(project.github_url)
  
  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
    <motion.div
      animate={{ 
        y: isHovered ? -8 : 0,
        scale: isHovered ? 1.01 : 1,
      }}
      transition={{ 
        type: "spring", 
        stiffness: 200, 
        damping: 20,
      }}
      initial={{ 
        rotateX: 0, 
        rotateY: 0,
        scale: 1
      }}
      className="relative group will-change-transform"
    >
      {/* 外发光效果 */}
      <motion.div
        className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-blue-500 to-purple-500 rounded-xl blur-sm opacity-0 group-hover:opacity-70 transition-all duration-500 pointer-events-none"
        animate={{
          scale: isHovered ? 1.02 : 1,
        }}
      />
      
      {/* 主卡片 */}
      <motion.div 
        className="relative card overflow-hidden bg-white dark:bg-dark-800 transform-gpu"
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          willChange: 'transform',
        }}
        animate={{
          rotateX: 0,
          rotateY: 0,
        }}
        transition={{ duration: 0 }}
      >
        {/* 动态背景网格 */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10 pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(35, 185, 232, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(35, 185, 232, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }} />
        </div>
        
        {/* 光束动画 */}
        <motion.div
          className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 group-hover:animate-pulse pointer-events-none"
          animate={{
            x: isHovered ? '400%' : '-100%',
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        />

        {/* 项目图片区域 */}
        <div className="relative aspect-video bg-gradient-to-br from-primary-100 via-blue-100 to-purple-100 dark:from-primary-900/50 dark:via-blue-900/50 dark:to-purple-900/50 overflow-hidden">
          {/* 缩略图 */}
          {project.thumbnail && (
            <img
              src={`/${project.thumbnail}`}
              alt={project.name}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
              onError={(e) => {
                // 图片加载失败时隐藏图片，显示默认背景
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          )}
          
          {/* 粒子效果背景 */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-primary-400 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  scale: [0, 1, 0],
                  opacity: [0, 0.6, 0],
                  y: [0, -20, -40],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.4,
                  ease: "easeOut",
                }}
              />
            ))}
          </div>
          
          {/* 项目图标 - 只在没有缩略图时显示 */}
          {!project.thumbnail && (
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="relative"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">
                    {project.name.charAt(0)}
                  </span>
                </div>
                
                {/* 环形光效 */}
                <motion.div
                  className="absolute inset-0 rounded-xl border-2 border-primary-400"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
            </div>
          )}
          
          {/* GitHub统计悬浮显示 */}
          <motion.div
            className="absolute top-3 right-3 flex gap-2 pointer-events-none z-10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: isHovered && githubStats && !statsLoading ? 1 : 0,
              scale: isHovered && githubStats && !statsLoading ? 1 : 0.8 
            }}
            transition={{ duration: 0.3 }}
          >
            {githubStats && !statsLoading && (
              <>
                <div className="bg-black/20 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 text-xs text-white">
                  <Star size={10} />
                  {githubStats.stars}
                </div>
                <div className="bg-black/20 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 text-xs text-white">
                  <GitFork size={10} />
                  {githubStats.forks}
                </div>
              </>
            )}
          </motion.div>

          {/* 悬浮操作 - 仅覆盖图片区域 */}
          <motion.div
            className="absolute inset-0 z-20 flex items-center justify-center"
            style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="absolute inset-0 bg-black/55 backdrop-blur-sm pointer-events-none" />
            <div className="relative flex items-center gap-3 pointer-events-auto">
              <motion.a
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors backdrop-blur-sm border border-white/20"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Github size={18} />
                GitHub
              </motion.a>
              
              {project.docs_url && (
                <motion.a
                  href={project.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-primary-500/80 hover:bg-primary-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors backdrop-blur-sm"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <BookOpen size={18} />
                  文档
                </motion.a>
              )}
            </div>
          </motion.div>

        </div>

        {/* 卡片内容 */}
        <div className="p-6 relative">
          {/* 标题区域 */}
          <div className="mb-3">
            <motion.h3 
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-primary-500 transition-colors"
              layoutId={`title-${project.name}`}
            >
              {project.name}
            </motion.h3>
            
            {/* 技术栈指示器 */}
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {githubStats && !statsLoading ? githubStats.language : 'Active Development'}
              </span>
              {statsLoading && (
                <span className="text-xs text-gray-400">Loading...</span>
              )}
            </div>
          </div>
          
          {/* 描述 */}
          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3 leading-relaxed">
            {project.description}
          </p>

          {/* 标签 */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.tags.map((tag, index) => (
              <motion.span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full border border-primary-200 dark:border-primary-800"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Tag size={10} />
                {tag}
              </motion.span>
            ))}
          </div>

          {/* 底部操作区 */}
            <div className="flex items-center justify-between">
              <motion.a
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Github size={16} />
                <span>Repository</span>
              </motion.a>
              
              {/* 只在有GitHub数据且不在加载时显示统计 */}
              {githubStats && !statsLoading && (
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <Star size={12} />
                    {githubStats.stars}
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye size={12} />
                    {githubStats.watchers}
                  </div>
                </div>
              )}
              
              {/* 加载状态指示器 */}
              {statsLoading && (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
                  <span>Loading stats...</span>
                </div>
              )}
            </div>          {/* 进度条装饰 */}
          <motion.div
            className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-500 via-blue-500 to-purple-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: isHovered ? "100%" : "0%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </motion.div>
    </div>
  )
}

export default ProjectCard
